/**
 * Precise note scheduling — the standard Web Audio "lookahead" pattern (a
 * fast timer repeatedly schedules everything due in the next N ms via
 * `AudioContext.currentTime` + `AudioParam`/`start(when)`, rather than
 * `setInterval` driving sound directly, which drifts under any GC pause or
 * tab-throttling). Everything in this file is pure arithmetic over plain
 * numbers and the injected `AudioClock`/`NotePlayer` interfaces — no
 * `AudioContext` anywhere, which is what makes it unit-testable with a fake
 * clock (`test/audio/sequencer.test.ts`).
 */
import type { Composition, DrumHit, NoteEvent } from './composition.js';

export function beatsToSeconds(beats: number, bpm: number): number {
  return (beats / bpm) * 60;
}

export function secondsToBeats(seconds: number, bpm: number): number {
  return (seconds * bpm) / 60;
}

interface Beated {
  readonly beat: number;
}

export interface DueEvent<T> {
  readonly event: T;
  /** The event's start time in beats, un-wrapped — how many loops have played plus its own position in this one. */
  readonly absoluteBeat: number;
}

/**
 * Every event in `track` whose start time falls in the half-open absolute-beat
 * window `[fromBeatAbs, toBeatAbs)`, against a loop of `loopBeats` beats.
 *
 * `track`'s own `beat` values are loop-relative (`[0, loopBeats)` —
 * `composition.ts`'s convention). A window can span an arbitrary number of
 * loop repeats (a long stall between scheduler ticks, or a test asking for a
 * whole loop at once), so this checks every lap the window could touch
 * rather than trying to fold the window itself into `[0, loopBeats)` — that
 * fold is exactly the kind of off-by-one the loop *seam* invites, and
 * checking laps directly sidesteps it.
 */
export function dueEvents<T extends Beated>(
  track: readonly T[],
  loopBeats: number,
  fromBeatAbs: number,
  toBeatAbs: number,
): DueEvent<T>[] {
  if (toBeatAbs <= fromBeatAbs || loopBeats <= 0) return [];
  const firstLap = Math.floor(fromBeatAbs / loopBeats) - 1;
  const lastLap = Math.floor(toBeatAbs / loopBeats) + 1;
  const out: DueEvent<T>[] = [];
  for (let lap = firstLap; lap <= lastLap; lap++) {
    const lapStart = lap * loopBeats;
    for (const event of track) {
      const absoluteBeat = lapStart + event.beat;
      if (absoluteBeat >= fromBeatAbs && absoluteBeat < toBeatAbs)
        out.push({ event, absoluteBeat });
    }
  }
  return out;
}

export interface AudioClock {
  /** Seconds, monotonic — `AudioContext.currentTime` in production. */
  now(): number;
}

export type NotePlayer = (
  event: NoteEvent | DrumHit,
  startSec: number,
  durationSec: number,
) => void;

export type MusicLayer = 'base' | 'danger';

const DEFAULT_DRUM_DURATION_BEATS = 0.12;

function durationBeatsOf(event: NoteEvent | DrumHit): number {
  return 'durationBeats' in event ? event.durationBeats : DEFAULT_DRUM_DURATION_BEATS;
}

/**
 * Schedules `composition`'s loop against an injected clock, calling
 * `playNote` for everything due within a `tick(lookaheadSec)` call's window.
 * Owns two monotonically increasing beat counters — `playedBeatAbs` (how far
 * playback has reached, advanced by real elapsed time each tick) and
 * `scheduledBeatAbs` (how far scheduling has reached, always ≥ played) —
 * rather than re-deriving position from `now() - startTime` every tick: a
 * live tempo change (`setLayer`) only has to affect the rate *from here*,
 * with no discontinuity in what has already been scheduled.
 */
export class MusicScheduler {
  private playedBeatAbs = 0;
  private scheduledBeatAbs = 0;
  private lastTickSec: number;
  private bpm: number;
  private layer: MusicLayer = 'base';

  constructor(
    private readonly composition: Composition,
    private readonly clock: AudioClock,
    private readonly playNote: NotePlayer,
  ) {
    this.lastTickSec = clock.now();
    this.bpm = composition.bpm;
  }

  get currentLayer(): MusicLayer {
    return this.layer;
  }

  get currentBpm(): number {
    return this.bpm;
  }

  /** Add or drop the danger arpeggio and nudge the tempo — takes effect from the next `tick()` on, never retroactively. */
  setLayer(layer: MusicLayer): void {
    if (this.layer === layer) return;
    this.layer = layer;
    this.bpm =
      layer === 'danger'
        ? this.composition.bpm * this.composition.dangerTempoMultiplier
        : this.composition.bpm;
  }

  /**
   * Start counting from "now" again, keeping the place in the loop.
   *
   * `AudioEngine.startMusic` calls this. Between a `stopMusic` and the next
   * `startMusic` nobody ticks, but the audio clock keeps running — so
   * without this the first tick after a gap would read the whole gap as
   * elapsed playback, and schedule every note of it at once, in the past.
   * The song resumes where it stopped rather than jumping ahead by however
   * long the player spent on a menu.
   */
  reset(): void {
    this.lastTickSec = this.clock.now();
    this.scheduledBeatAbs = this.playedBeatAbs;
  }

  /**
   * Schedule everything due within `lookaheadSec` of "now". Call this from a
   * fast, regular timer (25ms is the usual choice) with a lookahead a few
   * multiples of the timer interval (100-150ms) — the timer only decides
   * *when to check*, never when a note actually plays; that is always
   * `startSec`, handed to `playNote` for the caller to schedule precisely on
   * the real `AudioContext` clock.
   */
  tick(lookaheadSec: number): void {
    const now = this.clock.now();
    const dtSec = Math.max(0, now - this.lastTickSec);
    this.lastTickSec = now;
    this.playedBeatAbs += secondsToBeats(dtSec, this.bpm);

    const targetBeatAbs = this.playedBeatAbs + secondsToBeats(lookaheadSec, this.bpm);
    if (targetBeatAbs <= this.scheduledBeatAbs) return;

    // Never schedule into the past. Ordinarily `scheduledBeatAbs` is ahead
    // of `playedBeatAbs` by up to one lookahead, and this is a no-op; after
    // a stall longer than the lookahead (a throttled background timer) the
    // notes that would have played during it are skipped, not fired in a
    // burst the moment the timer wakes up.
    const fromBeatAbs = Math.max(this.scheduledBeatAbs, this.playedBeatAbs);
    const loopBeats = this.composition.bars * this.composition.beatsPerBar;
    const tracks: (readonly (NoteEvent | DrumHit)[])[] =
      this.layer === 'danger'
        ? [
            this.composition.melody,
            this.composition.bass,
            this.composition.chords,
            this.composition.percussion,
            this.composition.dangerLayer,
          ]
        : [
            this.composition.melody,
            this.composition.bass,
            this.composition.chords,
            this.composition.percussion,
          ];

    for (const track of tracks) {
      for (const { event, absoluteBeat } of dueEvents(
        track,
        loopBeats,
        fromBeatAbs,
        targetBeatAbs,
      )) {
        const beatsFromNow = absoluteBeat - this.playedBeatAbs;
        const startSec = now + beatsToSeconds(beatsFromNow, this.bpm);
        this.playNote(event, startSec, beatsToSeconds(durationBeatsOf(event), this.bpm));
      }
    }
    this.scheduledBeatAbs = targetBeatAbs;
  }
}
